import * as React from 'react';
import produce from 'immer';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@apollo/client';
import {
  Button,
  Heading,
  Modal,
  SpinnerOverlay,
  Text,
  useTheme,
} from '@quality24/design-system';
import classNames from 'classnames';

import { JsonAppFrontPageButton } from '@quality24/inpatient-typings';

import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import ActionCard from '@/inpatient-patient-pwa/molecules/ActionCard';
import SurveyNotificationAlert from '@/inpatient-patient-pwa/molecules/SurveyNotificationAlert';
import { Theme } from '@/inpatient-patient-pwa/types/theme';
import {
  getAppNotificationStatus,
  getRoomId,
  storeAppNotificationStatus,
  storeFloorId,
  storeRoomId,
} from '@/inpatient-patient-pwa/services/storage';
import useUserParams from '@/inpatient-patient-pwa/hooks/useUserParams';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';
import {
  ConciergeConditionInput,
  selectConciergeConfig,
} from '@/inpatient-patient-pwa/utils/conciergeCondition';
import useCustomData from '../../hooks/useCustomData';
import { SELECT_QUERY, SERVICES_QUERY, SURVEY_SUBSCRIPTION } from './graphql';
import { QueryResult, ServicesQueryResult, SubscriptionResult } from './types';

const handleSurveyChanges = (prev: QueryResult, props: SubscriptionResult) => {
  const survey =
    props.subscriptionData.data?.releasedSurveyChangesByAdmission
      .releasedSurvey;
  return produce(prev, (draft) => {
    // eslint-disable-next-line no-param-reassign
    draft.survey.nodes = survey ? [survey] : [];

    // eslint-disable-next-line no-param-reassign
    draft.survey.totalCount = survey ? 1 : 0;
  });
};

const HomePage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const { user } = useAuth();
  const { theme } = useTheme<Theme>();
  const { generalInfo, infrastructureCalls } = useCustomData();
  const admissionId = user?.admissionId || '';

  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`,
  });

  const userParams = useUserParams();

  const navigate = useNavigate();

  const [notificationSeen, setNotificationSeen] = React.useState(
    getAppNotificationStatus(),
  );

  const {
    data,
    subscribeToMore,
    loading: loadingPageQuery,
  } = useQuery<QueryResult>(SELECT_QUERY, {
    variables: {
      admissionId,
    },
    skip: !admissionId,
    fetchPolicy: 'network-only',
  });

  const { data: servicesQueryData, loading: loadingServicesQuery } =
    useQuery<ServicesQueryResult>(SERVICES_QUERY, {
      variables: {
        floorId: data?.admission?.room.floorId,
      },
      skip: !data?.admission?.room.floorId,
    });

  const feedbackConfigData = React.useMemo(
    () => servicesQueryData?.feedbackConfigs.nodes[0]?.data.displayOnConcierge,
    [servicesQueryData?.feedbackConfigs.nodes],
  );
  const infoSections = React.useMemo(
    () => servicesQueryData?.hospitalInformationSections.totalCount || 0,
    [servicesQueryData?.hospitalInformationSections.totalCount],
  );

  const conditionInput = React.useMemo<ConciergeConditionInput>(
    () => ({ floorType: data?.admission?.room.floor?.type ?? null }),
    [data?.admission?.room.floor?.type],
  );

  const appNotification = React.useMemo(() => {
    const candidates = data?.conciergeConfigs.nodes.filter(
      (n) => n.type === 'APP_NOTIFICATION',
    );
    const node = selectConciergeConfig(candidates ?? [], conditionInput);
    if (!node?.data) return undefined;

    // `data.titleI18N` / `data.contentI18N` are locale maps nested in a JSONB
    // column, so the API's `@i18n` plugin cannot resolve them. They take
    // precedence over the untranslated siblings, which older configs may still
    // be the only source for.
    return {
      title: resolveI18n(node.data.titleI18N) || node.data.title,
      content: resolveI18n(node.data.contentI18N) || node.data.content,
    };
  }, [data?.conciergeConfigs.nodes, conditionInput, resolveI18n]);

  // `undefined` means no APP_FRONT_PAGE config was selected for this floor —
  // the legacy hardcoded grid applies. A selected config with `buttons: []`
  // is a distinct state (deliberately empty grid) and must not fall back.
  const frontPageConfig = React.useMemo(() => {
    const candidates = data?.conciergeConfigs.nodes.filter(
      (n) => n.type === 'APP_FRONT_PAGE',
    );
    return selectConciergeConfig(candidates ?? [], conditionInput);
  }, [data?.conciergeConfigs.nodes, conditionInput]);

  const hasFrontPageConfig = !!frontPageConfig;
  const frontPageButtons: JsonAppFrontPageButton[] =
    frontPageConfig?.data?.buttons ?? [];

  const loading = loadingPageQuery || loadingServicesQuery;

  React.useEffect(() => {
    if (data?.admission?.room?.floorId) {
      storeFloorId(data?.admission?.room?.floorId);
    }
    if (data?.admission?.room?.id && !getRoomId()) {
      storeRoomId(data?.admission?.room?.id);
    }
  }, [data?.admission?.room?.floorId, data?.admission?.room?.id]);

  React.useEffect(() => {
    if (!admissionId) return () => null;

    const unsubscribe = subscribeToMore({
      document: SURVEY_SUBSCRIPTION,
      variables: { admissionId },
      updateQuery: handleSurveyChanges,
    });
    return unsubscribe;
  }, [admissionId, subscribeToMore]);

  const availableModules = user.modules ?? [];

  const modules = {
    assistance:
      availableModules.includes('PATIENT_CALL_ASSISTANCE') &&
      !!servicesQueryData?.assistanceServices.totalCount,
    infrastructure:
      availableModules.includes('PATIENT_CALL_INFRASTRUCTURE') &&
      !!servicesQueryData?.otherServices.totalCount,
  };
  const cardsCount =
    (modules.assistance ? 1 : 0) +
    (modules.infrastructure ? 1 : 0) +
    (feedbackConfigData ? 1 : 0) +
    (infoSections > 0 ? 1 : 0);

  const isSurveyExpired = React.useMemo(() => {
    const surveySchedulePeriodInDays =
      data?.survey?.nodes[0]?.survey.scheduleData?.frequency === 'periodic'
        ? data?.survey?.nodes[0]?.survey.scheduleData?.period || 1
        : 1;
    const createdAt = data?.survey.nodes[0]?.createdAt;
    const limitDate = moment().subtract(surveySchedulePeriodInDays, 'days');

    if (createdAt) {
      return moment(createdAt).diff(limitDate, 'days') < 0;
    }
    return true;
  }, [data]);

  const feedbackUrl = React.useMemo(() => {
    if (!feedbackConfigData) return undefined;

    // The URL constructor will throw an exception if the passed URL is not valid
    const url = new URL(feedbackConfigData.url || '');

    if (userParams && (feedbackConfigData.userParams?.length || 0) > 0) {
      feedbackConfigData.userParams?.forEach((param) => {
        const userParam = userParams[param.name];
        if (userParam) {
          url.searchParams.append(param.alias || param.name, userParam);
        }
      });
    }
    return url.toString();
  }, [feedbackConfigData, userParams]);

  const renderButton = (
    btn: JsonAppFrontPageButton,
    direction: 'column' | 'row',
  ) => {
    // `data.buttons[].nameI18N` is a locale map nested in a JSONB column, so the
    // API's `@i18n` plugin cannot resolve it. It takes precedence over the
    // untranslated `name`, which older configs may still be the only source for.
    const title = resolveI18n(btn.nameI18N) || btn.name;

    switch (btn.type) {
      case 'serviceGroup':
        return (
          <ActionCard
            title={title}
            icon={btn.icon}
            direction={direction}
            onSelect={() =>
              navigate(
                `/calls/group/${btn.groupId}?title=${encodeURIComponent(
                  title,
                )}`,
              )
            }
          />
        );
      case 'feedback':
        return (
          <ActionCard
            title={title}
            icon={btn.icon}
            direction={direction}
            onSelect={() => window.open(btn.linkUrl, '_blank')}
          />
        );
      case 'informationGroup':
        return (
          <ActionCard
            title={title}
            icon={btn.icon}
            direction={direction}
            onSelect={() => navigate(`/information?group_id=${btn.groupId}`)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('home.pageTitle')}</title>
      </Helmet>

      {loading && (
        <div className="px-4 d-flex flex-justify-center">
          <SpinnerOverlay />
        </div>
      )}

      {!loading && (
        <div className="px-4">
          <Heading as="h4" className="mb-4" size="lg1" weight="semiBold">
            {t('home.heading')}
          </Heading>
          <div className="row g-2">
            {/* Dynamic grid driven by the APP_FRONT_PAGE config */}
            {hasFrontPageConfig &&
              frontPageButtons.map((btn, idx) => {
                // On the mobile 2-column layout (≤ sm) an odd count leaves a
                // trailing card — make it span the full row (as a `row` card).
                // Above sm every card keeps the uniform col-sm-4 / col-lg-3
                // column layout.
                const isLastOdd =
                  frontPageButtons.length % 2 === 1 &&
                  idx === frontPageButtons.length - 1;
                const fullWidth = isMobile && isLastOdd;
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${btn.type}-${idx}`}
                    className={classNames(
                      fullWidth ? 'col-12' : 'col-6',
                      'col-sm-4 col-lg-3',
                    )}
                  >
                    {renderButton(btn, fullWidth ? 'row' : 'column')}
                  </div>
                );
              })}

            {/* Legacy hardcoded grid (fallback when no APP_FRONT_PAGE config) */}
            {!hasFrontPageConfig && modules.assistance && (
              <div key="assistance" className="col-6 col-sm-4 col-lg-3">
                <ActionCard
                  title={t('home.cards.nurse')}
                  icon="faUserNurse"
                  onSelect={() => navigate('/calls/assistance')}
                />
              </div>
            )}
            {!hasFrontPageConfig && modules.infrastructure && (
              <div key="infra" className="col-6 col-sm-4 col-lg-3">
                <ActionCard
                  title={t('home.cards.infrastructure', {
                    service: infrastructureCalls.toLowerCase(),
                  })}
                  icon="faUsers"
                  onSelect={() => navigate('/calls/others')}
                />
              </div>
            )}

            {!hasFrontPageConfig && feedbackConfigData && (
              <div
                key="feedback"
                className={classNames(
                  cardsCount % 2 === 0 || infoSections > 0 ? 'col-6' : 'col-12',
                  'col-sm-4 col-lg-3',
                )}
              >
                <ActionCard
                  title={
                    // `displayOnConcierge.buttonLabelI18N` is a locale map
                    // nested in a JSONB column, so the API cannot resolve it.
                    resolveI18n(feedbackConfigData.buttonLabelI18N) ||
                    feedbackConfigData.buttonLabel ||
                    t('home.cards.feedbackFallback')
                  }
                  icon={feedbackConfigData.buttonIcon || 'faMessage'}
                  onSelect={() => {
                    window.open(feedbackUrl, '_blank');
                  }}
                  direction={
                    isMobile && cardsCount % 2 === 0 ? 'column' : 'row'
                  }
                />
              </div>
            )}
            {!hasFrontPageConfig && infoSections > 0 && (
              <div
                key="info"
                className={classNames(
                  cardsCount % 2 === 0 ? 'col-6' : 'col-12',
                  'col-sm-4 col-lg-3',
                )}
              >
                <ActionCard
                  title={generalInfo}
                  icon="faInfoCircle"
                  onSelect={() => navigate('/information')}
                  direction={
                    isMobile && cardsCount % 2 === 0 ? 'column' : 'row'
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}

      {data?.survey?.nodes[0]?.status === 'PENDING' && !isSurveyExpired && (
        <div className="px-4">
          <SurveyNotificationAlert
            className="mt-4"
            survey={data.survey.nodes[0]}
            text={
              resolveI18n(
                data.survey.nodes[0]?.survey?.config.surveyNotificationTextI18N,
              ) || data.survey.nodes[0]?.survey?.config.surveyNotificationText
            }
          />
        </div>
      )}

      {/* app notification alert */}
      {appNotification && !notificationSeen && (
        <Modal
          id="confirmation-modal"
          show
          title={appNotification.title || undefined}
          onHide={() => {
            storeAppNotificationStatus();
            setNotificationSeen(true);
          }}
          footer={
            <div className="d-flex flex-justify-end gap-3">
              <Button
                onClick={() => {
                  storeAppNotificationStatus();
                  setNotificationSeen(true);
                }}
                size="lg"
              >
                OK
              </Button>
            </div>
          }
        >
          <Text>{appNotification.content}</Text>
        </Modal>
      )}
    </>
  );
};

export default HomePage;
